import { defineComponent as _defineComponent } from 'vue';
import { formatFieldValue } from "@/core/fields";
import { generateTableHead } from "@/core/table";
import { computed, nextTick, onMounted, onUnmounted, ref } from "vue";
import SortIndicator from "../components/SortIndicator.vue";
// Component props and emits
export default /*@__PURE__*/ _defineComponent({
    __name: 'DatasetTable',
    props: {
        data: { type: Array, required: true },
        columns: { type: Array, required: true },
        tableClasses: { type: String, required: false },
        headerClasses: { type: String, required: false },
        bodyClasses: { type: String, required: false },
        stickyHeader: { type: Boolean, required: false, default: false },
        stickyColumns: { type: Number, required: false, default: 0 },
        sorts: { type: Array, required: false, default: () => [] },
        zoom: { type: Number, required: false, default: 0.75 },
        pendingChanges: { type: Array, required: false, default: () => [] }
    },
    emits: ["toggleSort"],
    setup(__props, { expose: __expose, emit: __emit }) {
        __expose();
        const props = __props;
        const emit = __emit;
        // Refs
        const tableRef = ref(null);
        const headerRefs = ref([]);
        const columnWidths = ref([]);
        // Computed properties
        const tableHead = computed(() => {
            return generateTableHead(props.columns);
        });
        // Calculate the actual number of sticky columns
        // Groups cannot be sticky, so we limit to the first group or the stickyColumns prop
        const actualStickyColumns = computed(() => {
            if (!props.stickyColumns || props.stickyColumns <= 0) {
                return 0;
            }
            let stickyCount = 0;
            for (let i = 0; i < props.columns.length && i < props.stickyColumns; i++) {
                const column = props.columns[i];
                // If we encounter a group, stop - groups cannot be sticky
                if (column.type === "group") {
                    break;
                }
                stickyCount++;
            }
            return stickyCount;
        });
        // Calculate cumulative left positions for sticky columns
        const calculateStickyPositions = () => {
            if (!tableRef.value || actualStickyColumns.value === 0) {
                return;
            }
            nextTick(() => {
                const widths = [];
                // Get the actual widths of the header cells
                headerRefs.value.forEach((header) => {
                    if (header) {
                        widths.push(header.offsetWidth);
                    }
                });
                columnWidths.value = widths;
            });
        };
        // Lifecycle
        onMounted(() => {
            calculateStickyPositions();
            // Recalculate on window resize
            window.addEventListener("resize", calculateStickyPositions);
        });
        onUnmounted(() => {
            window.removeEventListener("resize", calculateStickyPositions);
        });
        // Sorting helper functions
        const isSortable = (column) => {
            if (column.type === "group")
                return false;
            return true;
        };
        const getSortState = (column) => {
            if (!column.key)
                return null;
            const sort = props.sorts.find((s) => s.field === column.key);
            return sort ? sort.direction : null;
        };
        const getSortOrder = (column) => {
            if (!column.key)
                return null;
            const index = props.sorts.findIndex((s) => s.field === column.key);
            return index !== -1 ? index + 1 : null;
        };
        const handleHeaderClick = (column) => {
            if (isSortable(column) && column.key) {
                emit("toggleSort", column.key);
            }
        };
        // Helper functions
        const getStickyLeftPosition = (index) => {
            if (index === 0)
                return 0;
            let leftPosition = 0;
            for (let i = 0; i < index; i++) {
                leftPosition += columnWidths.value[i] || 0;
            }
            return leftPosition;
        };
        const getCellClasses = (column, index) => {
            const classes = ["content-cell"];
            if (column.className)
                classes.push(column.className);
            if (column.align)
                classes.push(`text-${column.align}`);
            if (index < actualStickyColumns.value)
                classes.push("sticky-column");
            return classes.join(" ");
        };
        const getCellStyles = (column, index) => {
            const styles = {};
            if (index < actualStickyColumns.value) {
                const leftPosition = getStickyLeftPosition(index);
                styles.left = `${leftPosition}px`;
            }
            // Apply fixed width if specified
            if (column.width) {
                styles.width = column.width;
                styles.minWidth = column.width;
                styles.maxWidth = column.width;
            }
            return styles;
        };
        // Helper functions now use the cell's built-in field and columnIndex properties
        const getTableHeadCellClasses = (cell) => {
            const classes = ["header-cell"];
            const { field, columnIndex } = cell;
            if (field.className)
                classes.push(field.className);
            if (field.align)
                classes.push(`text-${field.align}`);
            if (field.color)
                classes.push("has-bg-color");
            if (columnIndex < actualStickyColumns.value) {
                classes.push("sticky-th");
            }
            if (isSortable(field)) {
                classes.push("sortable-header", "cursor-pointer");
            }
            return classes.join(" ");
        };
        const getHeaderStylesFromCell = (cell) => {
            const { field, columnIndex } = cell;
            const styles = {};
            if (columnIndex < actualStickyColumns.value) {
                const leftPosition = getStickyLeftPosition(columnIndex);
                styles.left = `${leftPosition}px`;
            }
            // Apply fixed width if specified
            if (field.width) {
                styles.width = field.width;
                styles.minWidth = field.width;
                styles.maxWidth = field.width;
            }
            if (field.color) {
                styles.backgroundColor = field.color;
            }
            return styles;
        };
        const handleHeaderClickFromCell = (cell) => {
            if (cell.field) {
                handleHeaderClick(cell.field);
            }
        };
        const isSortableFromCell = (cell) => {
            return cell.field ? isSortable(cell.field) : false;
        };
        const getSortStateFromCell = (cell) => {
            return cell.field ? getSortState(cell.field) : null;
        };
        const getSortOrderFromCell = (cell) => {
            return cell.field ? getSortOrder(cell.field) : null;
        };
        const getValue = (item, key) => {
            return key.split(".").reduce((obj, prop) => obj === null || obj === void 0 ? void 0 : obj[prop], item);
        };
        const formatValue = (value, field) => {
            if (value == null)
                return "-";
            // Custom formatter takes precedence
            if (field.formatter) {
                return field.formatter(value);
            }
            // Use the centralized field formatter
            return formatFieldValue(value, field);
        };
        function getColumnValue(item, column) {
            const value = getValue(item, column.key);
            return formatValue(value, column);
        }
        // Pending changes helpers
        const findPendingChangeForRow = (item) => {
            if (!props.pendingChanges || props.pendingChanges.length === 0) {
                return undefined;
            }
            return props.pendingChanges.find((change) => {
                if (change._row_id.delegacion && change._row_id.sala) {
                    return change._row_id.delegacion === item.delegacion &&
                        change._row_id.sala === item.sala &&
                        String(change._row_id.ano_natural) === String(item.ano_natural) &&
                        String(change._row_id.mes_natural) === String(item.mes_natural);
                }
                else if (change._row_id.distribuidor) {
                    return change._row_id.distribuidor === item.distribuidor &&
                        String(change._row_id.ano_natural) === String(item.ano_natural) &&
                        String(change._row_id.mes_natural) === String(item.mes_natural);
                }
                else if (!change._row_id.distributor && change._row_id.delegacion_zona_de_ventas) {
                    return change._row_id.delegacion_zona_de_ventas === item.delegacion_zona_de_ventas &&
                        Object.keys(item.periods).includes(change._row_id.mes_natural.toString().padStart(2, '0'));
                }
                else {
                    return change._row_id.distributor === item.distribuidor &&
                        change._row_id.delegacion_zona_de_ventas === item.delegacion_zona_de_ventas &&
                        String(change._row_id.ano_natural) === String(item.ano_natural) &&
                        String(change._row_id.mes_natural) === String(item.mes_natural);
                }
            });
        };
        const hasChangedValue = (item, fieldKey) => {
            const pendingChange = findPendingChangeForRow(item);
            // Exclude the _row_id field from being shown as a change
            return pendingChange ? fieldKey in pendingChange && fieldKey !== "_row_id" : false;
        };
        const getChangedValue = (item, column) => {
            const pendingChange = findPendingChangeForRow(item);
            if (!pendingChange || !(column.key in pendingChange)) {
                return "-";
            }
            const changedValue = pendingChange[column.key];
            return formatValue(changedValue, column);
        };
        const __returned__ = { props, emit, tableRef, headerRefs, columnWidths, tableHead, actualStickyColumns, calculateStickyPositions, isSortable, getSortState, getSortOrder, handleHeaderClick, getStickyLeftPosition, getCellClasses, getCellStyles, getTableHeadCellClasses, getHeaderStylesFromCell, handleHeaderClickFromCell, isSortableFromCell, getSortStateFromCell, getSortOrderFromCell, getValue, formatValue, getColumnValue, findPendingChangeForRow, hasChangedValue, getChangedValue, SortIndicator };
        Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true });
        return __returned__;
    }
});
